<script setup>
  const { page } = defineProps(['page']);

  function getYouTubeId(url) {
    const match = url?.match(/[?&]v=([^&]+)/);
    return match ? match[1] : '';
  }
</script>

<!----------------------------------------------------------------------------->

<template>
  <Section autowrap wide v-if="page.glory_unlimited_enabled">
    <BigTitle>Glory Unlimited</BigTitle>
    <ShowAnimation>
      <Foreground class="container">
        <GridItem :from="2" :to="11" class="wrapper">
          <Card>
            <div class="header">
              <div class="logo-col">
                <img :src="useCdn().generateStaticAsset('images/glory-unlimited.png')" alt="GLORY Unlimited" class="logo" />
              </div>
              <div class="content-col">
                <p class="description">{{ page.glory_unlimited_subtitle }}</p>
                <Button :to="page.glory_unlimited_button_link?.url ?? page.glory_unlimited_button_link">{{ page.glory_unlimited_button_label }}</Button>
              </div>
            </div>
            <div v-if="page.glory_unlimited_videos?.length" class="videos-container">
              <div class="content-cell">
                <p class="description">{{ page.glory_unlimited_subtitle }}</p>
                <Button :to="page.glory_unlimited_button_link?.url ?? page.glory_unlimited_button_link">{{ page.glory_unlimited_button_label }}</Button>
              </div>
              <a
                v-for="video in page.glory_unlimited_videos"
                :key="video.youtube_url"
                :href="video.youtube_url"
                target="_blank"
                rel="noopener noreferrer"
                class="video-card"
              >
                <div class="thumbnail-wrap">
                  <Picture v-if="video.thumbnail" :image="video.thumbnail" preset="card-small" class="thumbnail" />
                  <img
                    v-else
                    :src="`https://i.ytimg.com/vi/${getYouTubeId(video.youtube_url)}/maxresdefault.jpg`"
                    :alt="video.title"
                    class="thumbnail"
                  />
                  <div class="play-overlay">
                    <svg class="play-icon" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="30" cy="30" r="30" fill="rgba(0,0,0,0.6)" />
                      <polygon points="24,18 46,30 24,42" fill="white" />
                    </svg>
                  </div>
                </div>
                <p class="video-title">{{ video.title }}</p>
              </a>
            </div>
          </Card>
        </GridItem>
      </Foreground>
    </ShowAnimation>
  </Section>
</template>

<!----------------------------------------------------------------------------->

<style lang="scss" scoped>
  section {
    padding-top: px(90);
    padding-bottom: px(128);
    margin-bottom: -$content-padding-bottom;

    background-color: $black;

    .container {
      @include grid;
    }

    .card {
      padding-top: px(74);
      overflow: hidden;

      .header {
        display: flex;
        align-items: center;
        gap: px(80);
        padding: 0 px(108) px(56);
      }

      .logo-col {
        flex: 0 0 auto;

        .logo {
          width: px(300);
          display: block;
        }
      }

      .content-col {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: px(36);

        .description {
          @include heroic-bold(px(28));
          color: $white;
          max-width: px(500);
          line-height: 1.45;
        }
      }

      .videos-container {
        display: flex;
        gap: 0;
        border-top: px(1) solid $black-5;

        .content-cell {
          display: none;
        }

        .video-card {
          flex: 1;
          display: flex;
          flex-direction: column;
          text-decoration: none;
          border-right: px(1) solid $black-5;

          &:last-child {
            border-right: none;
          }

          .thumbnail-wrap {
            position: relative;
            overflow: hidden;
            aspect-ratio: 16 / 9;
            background-color: $black-5;

            .thumbnail {
              width: 100%;
              height: 100%;
              object-fit: cover;
              display: block;
              transform: scale(1);
              transition: transform 0.4s $quadOut;

              &:deep(img) {
                transform: scale(1);
                transition: transform 0.4s $quadOut;
              }
            }

            .play-overlay {
              position: absolute;
              inset: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              opacity: 0;
              transition: opacity 0.25s $sineOut;

              .play-icon {
                width: px(60);
                height: px(60);
              }
            }
          }

          .video-title {
            @include heroic-bold(px(18));
            color: $white;
            padding: px(20) px(24) px(24);
            line-height: 1.3;
            flex: 1;
          }

          @include hover {
            .thumbnail-wrap {
              .thumbnail {
                transform: scale(1.05);
                transition: transform 0.5s $quadOut;

                &:deep(img) {
                  transform: scale(1.05);
                  transition: transform 0.5s $quadOut;
                }
              }

              .play-overlay {
                opacity: 1;
              }
            }

            .video-title {
              color: $grey-1;
            }
          }
        }
      }
    }

    @include break-large {
      .card {
        .header {
          gap: px(56);
          padding: 0 px(64) px(48);
        }

        .logo-col .logo {
          width: px(240);
        }

        .content-col .description {
          font-size: px(24);
        }
      }
    }

    @include break-medium {
      .card {
        .header {
          padding: 0 px(48) px(48);
        }

        .logo-col .logo {
          width: px(220);
        }

        .content-col {
          display: none;
        }

        .videos-container {
          flex-wrap: wrap;

          .content-cell {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            gap: px(32);
            flex: 0 0 50%;
            padding: px(40) px(40);
            border-right: px(1) solid $black-5;
            border-bottom: px(1) solid $black-5;

            .description {
              @include heroic-bold(px(20));
              color: $grey-1;
              line-height: 1.45;
            }
          }

          .video-card {
            flex: 0 0 50%;
            border-right: none;
            border-bottom: px(1) solid $black-5;

            &:nth-child(even) {
              border-right: px(1) solid $black-5;
            }

            &:nth-last-child(-n+2) {
              border-bottom: none;
            }
          }
        }
      }
    }

    @include break-small {
      .wrapper {
        @include column(1, 12);
      }

      .card {
        .header {
          padding: 0 px(32) px(40);
        }

        .content-col .description {
          font-size: px(20);
        }

        .videos-container {
          flex-direction: column;

          .content-cell .description {
            font-size: px(22);
          }

          .video-card {
            flex: none;
            border-right: none;
            border-bottom: px(1) solid $black-5;

            &:nth-child(odd) {
              border-right: none;
            }

            &:last-child {
              border-bottom: none;
            }
          }
        }
      }
    }

    @include break-phone {
      .card {
        .header {
          padding: 0 px(28) px(36);
        }

        .logo-col .logo {
          width: px(180);
        }

        .content-col .description {
          font-size: px(18);
        }

        .videos-container .content-cell .description {
          font-size: px(22);
        }

        .videos-container .video-card .video-title {
          font-size: px(16);
          padding: px(16) px(20) px(20);
        }
      }
    }
  }
</style>
